import {
  invitationPreviewSchema,
  meSchema,
  peopleResponseSchema,
  sessionResponseSchema,
  type SessionResponse,
  siteResponseSchema,
} from '@wednesday/contracts';
import { queryOptions } from '@tanstack/react-query';

import { apiRequest } from '@/shared/api/client';

/** Server calls of the authentication area (api-design §3.2). */

export const sessionQueryKey = ['session'] as const;

/** The signed-in user + org + labels; the app shell needs it on every page. */
export const sessionQueryOptions = queryOptions({
  queryKey: sessionQueryKey,
  queryFn: ({ signal }) =>
    apiRequest('GET', '/auth/session', { schema: sessionResponseSchema, signal }),
  staleTime: 5 * 60_000,
});

export function login(body: { email: string; password: string }): Promise<SessionResponse> {
  return apiRequest('POST', '/auth/login', { body, schema: sessionResponseSchema });
}

/** D49: whether this site is the public demo. It never changes while the page is open. */
export const siteQueryOptions = queryOptions({
  queryKey: ['site'],
  queryFn: ({ signal }) => apiRequest('GET', '/site', { schema: siteResponseSchema, signal }),
  staleTime: Infinity,
});

/** DEMO-1: the public demo signs its visitor in without a form. */
export function openDemo(): Promise<SessionResponse> {
  return apiRequest('POST', '/auth/demo', { schema: sessionResponseSchema });
}

export function logout(): Promise<undefined> {
  return apiRequest('POST', '/auth/logout');
}

/**
 * D45: an Admin or Manager signs in as this person — sees what they see and makes changes for them,
 * each recorded as made by the Admin or Manager — until they go back or an hour passes.
 */
export function startViewAs(userId: string): Promise<undefined> {
  return apiRequest('POST', '/auth/view-as', { body: { userId } });
}

/** D45: whom you may sign in as: anyone for an Admin, their team for a Manager. */
export const viewAsPeopleQueryOptions = queryOptions({
  queryKey: ['view-as-people'],
  queryFn: ({ signal }) =>
    apiRequest('GET', '/auth/view-as/people', { schema: peopleResponseSchema, signal }),
  staleTime: 0,
});

export function stopViewAs(): Promise<undefined> {
  return apiRequest('DELETE', '/auth/view-as');
}

export function requestPasswordReset(email: string): Promise<undefined> {
  return apiRequest('POST', '/auth/password/forgot', { body: { email } });
}

export function resetPassword(token: string, password: string): Promise<undefined> {
  return apiRequest('POST', '/auth/password/reset', { body: { token, password } });
}

export function changePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<undefined> {
  return apiRequest('POST', '/auth/password/change', { body });
}

export const invitationQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['invitation', token],
    queryFn: ({ signal }) =>
      apiRequest('GET', `/invitations/${encodeURIComponent(token)}`, {
        schema: invitationPreviewSchema,
        signal,
      }),
    retry: false,
  });

export function acceptInvitation(
  token: string,
  body: { name: string; password: string },
): Promise<SessionResponse> {
  return apiRequest('POST', `/invitations/${encodeURIComponent(token)}/accept`, {
    body,
    schema: sessionResponseSchema,
  });
}

export function updateMe(body: { name?: string; timezone?: string }) {
  return apiRequest('PATCH', '/me', { body, schema: meSchema });
}
